import { ScrollStrategy, ScrollStrategyOptions } from "@angular/cdk/overlay";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { Router } from "@angular/router";
import { injectQueryClient } from "@ngneat/query";
import { environment } from "@env/environment";
import { ClientDataComponent } from "@features/search/components/client-data/client-data.component";
import { HorusDateRangeComponent } from "@shared/components/domain/horus-date-range/horus-date-range.component";
import { HorusLoadingComponent } from "@shared/components/domain/horus-loading/horus-loading.component";
import { HorusMessageGeneralComponent } from "@shared/components/domain/horus-message-general/horus-message-general.component";
import { SelectTenantsComponent } from "@shared/components/domain/select-tenants/select-tenants.component";
import { ToggleClassOnValueDirective } from "app/shared/directives/toggle-class-on-value.directive";
import { ClientData } from "../../../../shared/models/ClientData";
import { ClientCreationComponent } from "../client-creation/client-creation.component";
import { TenantUrlUtilService } from "@shared/services/tenant-url-util.service";
import { isEmpty } from "radash";
import { SearchState } from "../../services/search.state";
import { HorusBtnDirective } from "../../../../shared/directives/horus-btn.directive";
import { SettingsService } from "@core/services/settings.service";
import { UtilService } from "@core/utils/util.service";

@Component({
  selector: "app-search-data",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    SelectTenantsComponent,
    MatFormFieldModule,
    ReactiveFormsModule,
    HorusDateRangeComponent,
    MatInputModule,
    MatButtonModule,
    HorusMessageGeneralComponent,
    ClientDataComponent,
    ToggleClassOnValueDirective,
    HorusLoadingComponent,
    HorusBtnDirective,
  ],
  templateUrl: "./search-data.component.html",
  styleUrl: "./search-data.component.scss",
})
export class SearchDataComponent implements OnInit, AfterViewInit {
  readonly searchState = inject(SearchState);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly tenantUrlUtil = inject(TenantUrlUtilService);
  private readonly scrollStrategyOptions = inject(ScrollStrategyOptions);
  private readonly settingsService = inject(SettingsService);
  private readonly utilService = inject(UtilService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly queryClient = injectQueryClient();

  searchDataForm: FormGroup;
  scrollStrategy: ScrollStrategy;
  private readonly formValue;
  readonly isDisabled: any;
  readonly searchIcon: any;
  private lastFormValue: any = {};
  private isComponentInitialized = false;
  private lastTenantId: string | null = null;

  constructor(private fb: FormBuilder) {
    this.searchDataForm = this.fb.group({
      tenant: [{}],
      dateRange: [""],
      searchValue: [""],
    });

    this.scrollStrategy = this.scrollStrategyOptions.reposition();

    this.formValue = toSignal(this.searchDataForm.valueChanges, {
      initialValue: this.searchDataForm.value,
    });

    this.isDisabled = computed(() => {
      const form = this.formValue();

      if (!form) return true;

      const isInvalidSearchValue = form.searchValue && 
        this.utilService.getTypeData(form.searchValue) === 'error';

      return (
        !form.tenant ||
        Object.keys(form.tenant).length === 0 ||
        !form.dateRange ||
        form.dateRange === "" ||
        !form.searchValue ||
        form.searchValue === "" ||
        isInvalidSearchValue
      );
    });

    this.searchIcon = computed(() => {
      const form = this.formValue();
      const value = form?.searchValue || "";

      if (!value) return undefined;

      const searchType = this.utilService.getTypeData(value);
      return (
        environment.commons.searchTypeAndIcon.get(searchType) ||
        "searchErrorIcon"
      );
    });
  }

  ngOnInit(): void {
    const urlParams = this.tenantUrlUtil.getQueryParams();
    console.log("url params", urlParams);
    if (urlParams.searchValue || urlParams.dateRange || urlParams.tenant) {
      const tenantFromUrl = urlParams.tenant
        ? environment.tenants.find(
            (t) => t.tenantId.toLowerCase() === urlParams.tenant!.toLowerCase(),
          )
        : null;

      const dateRangeValue = this.tenantUrlUtil.getDateRangeOrDefault();

      this.searchDataForm.patchValue(
        {
          searchValue: urlParams.searchValue || "",
          dateRange: dateRangeValue,
          tenant: tenantFromUrl,
        },
        { emitEvent: false }
      );
    } else {
      this.searchDataForm.patchValue(
        {
          dateRange: this.searchState.dateRange(),
          tenant: this.searchState.selectedTenant(),
          searchValue: this.searchState.searchValue(),
        },
        { emitEvent: false }
      );
    }

    this.lastFormValue = this.searchDataForm.value;

    this.searchDataForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((formValue) => {
        const isValidSearchValue = formValue.searchValue && 
          this.utilService.getTypeData(formValue.searchValue) !== 'error';

        if (
          formValue.tenant &&
          Object.keys(formValue.tenant).length > 0 &&
          formValue.dateRange &&
          formValue.dateRange !== "" &&
          formValue.searchValue &&
          formValue.searchValue !== "" &&
          isValidSearchValue &&
          formValue.dateRange !== this.lastFormValue.dateRange
        ) {
          this.autoSearch();
        }

        this.lastFormValue = { ...formValue };
      });

    this.searchDataForm.controls["searchValue"].valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.settingsService.remove("lastOrdersRoute");
      });

    this.searchDataForm.controls["tenant"].valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((newTenant) => {
        if (this.isComponentInitialized && newTenant && newTenant.tenantId) {
          const currentTenant = this.tenantUrlUtil.currentTenant();
          if (
            currentTenant !== newTenant.tenantId &&
            this.lastTenantId !== newTenant.tenantId
          ) {
            this.updateTenantInCurrentUrl(newTenant.tenantId);
            this.lastTenantId = newTenant.tenantId;
          }
        }
      });
  }

  ngAfterViewInit(): void {
    this.searchDataForm.controls["tenant"].updateValueAndValidity();
    this.searchDataForm.controls["dateRange"].updateValueAndValidity();
    this.searchDataForm.controls["searchValue"].updateValueAndValidity();
    this.isComponentInitialized = true;
    this.lastTenantId = this.tenantUrlUtil.currentTenant();
  }

  searchClientData(): void {
    const form = this.getFormValues();

    const isInvalidSearchValue = this.utilService.getTypeData(form.searchValue) === 'error';

    if (
      !form.tenant ||
      Object.keys(form.tenant).length === 0 ||
      !form.dateRange ||
      form.dateRange === "" ||
      !form.searchValue ||
      form.searchValue === "" ||
      isInvalidSearchValue
    ) {
      return;
    }

    this.settingsService.remove("lastOrdersRoute");

    const tenantId = form.tenant?.tenantId;

    this.queryClient.invalidateQueries({ queryKey: ["orders", "groups"] });

    const queryParams = this.tenantUrlUtil.updateQueryParams({
      searchValue: form.searchValue,
      dateRange: form.dateRange,
      tenant: tenantId,
      typeRedirect: null,
    });

    this.searchState.updateSearchForm({
      ...form,
      currentPage: this.getCurrentPage(),
    });

    this.handleNavigation(form.searchValue, tenantId, queryParams);

    this.searchState.close();
  }

  autoSearch(): void {
    const form = this.getFormValues();

    const isInvalidSearchValue = this.utilService.getTypeData(form.searchValue) === 'error';

    if (
      !form.tenant ||
      Object.keys(form.tenant).length === 0 ||
      !form.dateRange ||
      form.dateRange === "" ||
      !form.searchValue ||
      form.searchValue === "" ||
      isInvalidSearchValue
    ) {
      return;
    }

    this.settingsService.remove("lastOrdersRoute");

    const tenantId = form.tenant?.tenantId;

    this.queryClient.invalidateQueries({ queryKey: ["orders", "groups"] });

    const queryParams = this.tenantUrlUtil.updateQueryParams({
      searchValue: form.searchValue,
      dateRange: form.dateRange,
      tenant: tenantId,
      typeRedirect: null,
    });

    this.searchState.updateSearchForm({
      ...form,
      currentPage: this.getCurrentPage(),
    });

    this.handleNavigation(form.searchValue, tenantId, queryParams);
  }

  deleteSearch(): void {
    this.searchDataForm.controls["searchValue"].setValue("");
    this.searchState.setSearchValue("");
  }

  async openClientCreationDialog(): Promise<void> {
    const formValue = this.searchDataForm.value;
    const phone = await this.searchState.getPhoneParamsUrl();
    const creationClientData: ClientData = {
      tenant: formValue.tenant,
      phone: phone || "",
    };
    const icon = this.searchState.searchIcon();
    if ("searchByIdentityDocumentIcon" === icon) {
      creationClientData.document = formValue.searchValue;
    } else if ("searchByEmailIcon" === icon) {
      creationClientData.email = formValue.searchValue;
    }
    const dialogRef = this.dialog.open(ClientCreationComponent, {
      height: "100%",
      position: { right: "0px" },
      scrollStrategy: this.scrollStrategy,
      data: creationClientData,
      disableClose: true,
      restoreFocus: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result !== undefined) {
        if (typeof result === "string") {
          this.searchClientData();
        }
      }
    });
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData("text") || "";
    const trimmedText = pastedText.trim();

    this.searchDataForm.controls["searchValue"].setValue(trimmedText);

    const isInvalidSearchValue = this.utilService.getTypeData(trimmedText) === 'error';

    if (!isInvalidSearchValue) {
      setTimeout(() => {
        this.searchClientData();
      }, 0);
    }
  }

  private handleNavigation(
    searchValue: string,
    tenantId: string,
    queryParams: Record<string, any>,
  ): void {
    const documentType = this.tenantUrlUtil.getDocumentType(searchValue);
    const currentPage = this.getCurrentPage();

    if (currentPage === "orders") {
      this.navigateForOrdersPage(
        documentType,
        tenantId,
        searchValue,
        queryParams,
      );
    } else {
      if (documentType === "ORDER_ID" || documentType === "SALE_NOTE_NUMBER") {
        const orderType = this.tenantUrlUtil.getOrderType(documentType as any);
        const orderRoute = this.router
          .createUrlTree(
            ["orders", tenantId, searchValue, orderType, "detail"],
            { queryParams },
          )
          .toString();
        this.settingsService.set("lastOrdersRoute", orderRoute);
      }

      const nextRoute = isEmpty(currentPage) ? "orders" : currentPage;
      this.tenantUrlUtil.navigateToTenant(
        [nextRoute, ":tenantId"],
        tenantId,
        queryParams,
      );
    }
  }

  private navigateForOrdersPage(
    documentType: string,
    tenantId: string,
    searchValue: string,
    queryParams: Record<string, any>,
  ): void {
    switch (documentType) {
      case "EMAIL":
      case "IDENTITY_DOCUMENT":
        this.tenantUrlUtil.navigateToTenant(
          ["orders", ":tenantId"],
          tenantId,
          queryParams,
        );
        break;

      case "ORDER_ID":
      case "SALE_NOTE_NUMBER":
        const orderType = this.tenantUrlUtil.getOrderType(documentType as any);
        this.router.navigate(
          ["orders", tenantId, searchValue, orderType, "detail"],
          { queryParams },
        );
        break;

      default:
        this.tenantUrlUtil.navigateToTenant(
          ["orders", ":tenantId"],
          tenantId,
          queryParams,
        );
        break;
    }
  }

  private getFormValues(): {
    tenant: any;
    dateRange: string;
    searchValue: string;
  } {
    const { tenant, dateRange, searchValue } = this.searchDataForm.value;
    return {
      tenant,
      dateRange,
      searchValue,
    };
  }

  private getCurrentPage(): string {
    const tree = this.router.parseUrl(this.router.url);
    return (
      tree.root.children["primary"]?.segments[0]?.path?.toLowerCase() ?? ""
    );
  }

  private updateTenantInCurrentUrl(newTenantId: string): void {
    const currentUrl = this.router.url;
    const urlTree = this.router.parseUrl(currentUrl);
    const currentPath = this.getCurrentPagePath();

    if (currentPath) {
      // Reutilizar el método existente del servicio
      this.tenantUrlUtil.navigateToTenant(
        [currentPath, ":tenantId"],
        newTenantId,
        urlTree.queryParams,
      );
    }
  }

  private getCurrentPagePath(): string | null {
    const tree = this.router.parseUrl(this.router.url);
    const primarySegments = tree.root.children["primary"]?.segments;

    if (!primarySegments || primarySegments.length === 0) {
      return "orders"; // default
    }

    // Obtener la ruta base (primera parte de la URL)
    return primarySegments[0]?.path || "orders";
  }
}
